import React, { ReactNode, useState } from "react";
import { isFieldHidden, formatCurrencyInput, parseCurrency } from "@util/helper";
import DragAndDropFileInput from "./DragAndDropFileInput";
import styled from "styled-components";
import type { UploadedFileRecord } from "@util/fileUploads";
import { getFieldKey } from "@util/formUtils";

// Re-export from formUtils so existing imports from this file keep working.
export type { TableColumn, PermitFormField } from "@util/formUtils";

function normalizeMaxCharacters(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value !== "number") return undefined;
  if (!Number.isFinite(value)) return undefined;
  const int = Math.floor(value);
  if (int <= 0) return undefined;
  return int;
}

function CharacterCount({ current, max }: { current: number; max: number }) {
  const remaining = max - current;
  const over = remaining < 0;
  return (
    <div className={`char-count ${over ? "over" : ""}`.trim()} aria-live="polite">
      {current}/{max} ({remaining} left)
    </div>
  );
}


interface FieldWrapperProps {
  children: ReactNode;
  fullWidth?: boolean;
  error?: string | null;
  className?: string;
}

/** Wraps a single form field for grid layout and error styling. Reusable across forms. */
export const FieldWrapper = ({
  children,
  fullWidth = false,
  error,
  className = "",
}: FieldWrapperProps) => (
  <div
    className={`form-field ${fullWidth ? "full-width" : "half-width"} ${error ? "error" : ""
      } ${className}`.trim()}
    data-error={error ? "true" : undefined}
  >
    {children}
  </div>
);

const TableFieldWrapper = styled.div`
  .table-container {
    overflow-x: auto;
    margin-top: 0.5rem;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #ccc;
  }

  th,
  td {
    padding: 0.75rem;
    border: 1px solid #ccc;
    text-align: left;
  }

  th {
    background-color: #f5f5f5;
    font-weight: 600;
    position: sticky;
    top: 0;
    white-space: normal;
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;
    height: auto;
    min-height: 2.5rem;
    vertical-align: top;
  }

  td input {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
  }

  td input.error {
    border: 2px solid #e74c3c;
  }

  .table-actions {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }

  .table-actions button {
    padding: 0.5rem 1rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    background: white;
    cursor: pointer;
    font-size: 0.875rem;
  }

  .table-actions button:hover {
    background: #f5f5f5;
  }

  .table-actions button.remove-row {
    color: #e74c3c;
    border-color: #e74c3c;
  }

  .table-actions button.remove-row:hover {
    background: #fee;
  }

  .table-row-error {
    color: #e74c3c;
    font-size: 0.875rem;
    margin-top: 0.25rem;
  }

  /* Mobile responsive: Transform table into card layout */
  @media (max-width: 768px) {
    table,
    thead,
    tbody,
    th,
    td,
    tr {
      display: block;
    }

    thead tr {
      position: absolute;
      top: -9999px;
      left: -9999px;
    }

    tbody tr {
      border: 1px solid #ccc;
      border-radius: 8px;
      margin-bottom: 1rem;
      padding: 0.75rem;
      background: white;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      min-height: auto;
      height: auto;
    }

    tbody tr:last-child {
      margin-bottom: 0;
    }

    td {
      border: none;
      border-bottom: 1px solid #eee;
      position: relative;
      padding: 0.75rem 0;
      min-height: auto;
      height: auto;
      display: flex;
      flex-direction: column;
    }

    td:last-child {
      border-bottom: none;
      padding-top: 0.75rem;
      text-align: right;
      min-height: auto;
      flex-direction: row;
      justify-content: flex-end;
    }

    td:before {
      content: attr(data-label);
      white-space: normal;
      word-wrap: break-word;
      word-break: break-word;
      overflow-wrap: break-word;
      font-weight: 600;
      color: #2c3e50;
      font-size: 0.75rem;
      line-height: 1.4;
      margin-bottom: 0.5rem;
      display: block;
    }

    td input {
      width: 100%;
      margin: 0;
    }

    .table-amount-input {
      position: relative;
      width: 100%;
      display: block;
    }

    .table-amount-prefix {
      position: absolute;
      left: 0.75rem;
      top: 50%;
      transform: translateY(-50%);
      color: #b0b0b0;
      font-size: 0.85rem;
      line-height: 1;
      font-weight: 600;
      pointer-events: none;
      white-space: nowrap;
      z-index: 1;
    }

    .table-amount-input input {
      width: 100%;
      padding-left: 3.25rem;
      margin: 0;
      box-sizing: border-box;
      display: block;
    }

    .table-actions {
      margin-top: 1rem;
    }
  }
`;

/** Amount field component with currency formatting */
const AmountField = ({
  fieldName,
  labelContent,
  value,
  onChange,
  error,
  sharedProps,
}: {
  fieldName: string;
  labelContent: ReactNode;
  value: string | undefined;
  onChange: (value: string | null) => void;
  error?: string | null;
  sharedProps: any;
}) => {
  const [displayValue, setDisplayValue] = useState(() => {
    if (!value) return "";
    const num = parseCurrency(value);
    return num > 0 ? formatCurrencyInput(String(num)) : "";
  });

  // Update display value when external value changes
  React.useEffect(() => {
    if (!value) {
      setDisplayValue("");
    } else {
      const num = parseCurrency(value);
      setDisplayValue(num > 0 ? formatCurrencyInput(String(num)) : "");
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Allow empty input
    if (!inputValue) {
      setDisplayValue("");
      onChange("");
      return;
    }

    // Remove any currency formatting for storage
    const cleaned = inputValue.replace(/NZ\$/gi, "").replace(/[,\s]/g, "").trim();

    // Validate it's a valid number format (allows partial input like "12.")
    if (/^\d*\.?\d*$/.test(cleaned)) {
      // Format for display
      const formatted = formatCurrencyInput(cleaned);
      setDisplayValue(formatted);

      // Store the cleaned numeric value
      const num = parseCurrency(cleaned);
      onChange(num > 0 ? String(num) : cleaned);
    }
  };

  const handleBlur = () => {
    // On blur, ensure proper formatting
    if (value) {
      const num = parseCurrency(value);
      if (num > 0) {
        const formatted = formatCurrencyInput(String(num));
        setDisplayValue(formatted);
        onChange(String(num));
      }
    }
  };

  return (
    <>
      <label htmlFor={fieldName}>{labelContent}</label>
      <div className="amount-input">
        <span className="amount-prefix">NZ$</span>
        <input
          {...sharedProps}
          type="text"
          inputMode="decimal"
          step="0.01"
          min="0"
          placeholder="0.00"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </div>
      {error && <span className="error-message">{error}</span>}
    </>
  );
};

/** Table field component for rendering editable tables */
const TableField = ({
  field,
  value,
  onChange,
  error,
}: {
  field: PermitFormField;
  value: Array<Record<string, string>> | undefined;
  onChange: (value: Array<Record<string, string>> | null) => void;
  error?: string | null;
}) => {
  const columns = (field.tableColumns || []).filter((col): col is TableColumn => col != null);
  const rows = Array.isArray(value) ? value : [];

  const addRow = () => {
    const newRow: Record<string, string> = {};
    columns.forEach(col => {
      if (col.id) {
        newRow[col.id] = "";
      }
    });
    onChange([...rows, newRow]);
  };

  const removeRow = (index: number) => {
    const newRows = rows.filter((_, i) => i !== index);
    onChange(newRows.length > 0 ? newRows : null);
  };

  const updateCell = (rowIndex: number, columnId: string, newValue: string, columnType?: string) => {
    const newRows = [...rows];
    if (!newRows[rowIndex]) {
      newRows[rowIndex] = {};
    }

    // For amount fields, store the cleaned numeric value
    if (columnType === "amount") {
      if (!newValue) {
        newRows[rowIndex][columnId] = "";
      } else {
        const cleaned = newValue.replace(/NZ\$/gi, "").replace(/[,\s]/g, "").trim();
        if (/^\d*\.?\d*$/.test(cleaned)) {
          const num = parseCurrency(cleaned);
          newRows[rowIndex][columnId] = num > 0 ? String(num) : cleaned;
        } else {
          newRows[rowIndex][columnId] = newValue;
        }
      }
    } else {
      newRows[rowIndex][columnId] = newValue;
    }

    onChange(newRows);
  };

  if (columns.length === 0) {
    return (
      <>
        <label>{field.label || ""}</label>
        <div className="error-message">Table columns are not configured</div>
      </>
    );
  }

  return (
    <TableFieldWrapper>
      <label>
        {field.label || ""}
        {field.required && (
          <span className="form-field-required" aria-hidden="true">
            {" "}
            *
          </span>
        )}
      </label>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col.id || ""}>
                  {col.label || col.id || ""}
                  {col.required && (
                    <span className="form-field-required" aria-hidden="true">
                      {" "}
                      *
                    </span>
                  )}
                </th>
              ))}
              <th style={{ width: "100px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map(col => {
                  const columnId = col.id || "";
                  const cellValue = row[columnId] || "";
                  const columnType = col.type || "text";
                  const inputType = columnType === "amount" ? "text" : columnType === "number" ? "number" : columnType;
                  const isRequired = col.required || false;
                  const hasError = isRequired && !cellValue.trim();
                  const columnLabel = col.label || col.id || "";
                  const columnMaxCharacters = normalizeMaxCharacters(col.maxCharacters);

                  // Format display value for amount fields
                  const displayValue = columnType === "amount" && cellValue
                    ? formatCurrencyInput(cellValue)
                    : cellValue;

                  const handleCellChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                    const inputValue = e.target.value;
                    if (columnType === "amount") {
                      if (!inputValue) {
                        updateCell(rowIndex, columnId, "", columnType);
                        return;
                      }
                      const cleaned = inputValue.replace(/NZ\$/gi, "").replace(/[,\s]/g, "").trim();
                      if (/^\d*\.?\d*$/.test(cleaned)) {
                        updateCell(rowIndex, columnId, cleaned, columnType);
                      }
                    } else {
                      const nextValue = columnMaxCharacters ? inputValue.slice(0, columnMaxCharacters) : inputValue;
                      updateCell(rowIndex, columnId, nextValue, columnType);
                    }
                  };

                  const handleCellBlur = () => {
                    if (columnType === "amount" && cellValue) {
                      const num = parseCurrency(cellValue);
                      if (num > 0) {
                        updateCell(rowIndex, columnId, String(num), columnType);
                      }
                    }
                  };

                  return (
                    <td key={columnId} data-label={columnLabel}>
                      {columnType === "amount" ? (
                        <div className="table-amount-input" style={{ position: "relative", width: "100%" }}>
                          <span
                            className="table-amount-prefix"
                            style={{
                              position: "absolute",
                              left: "0.75rem",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#b0b0b0",
                              fontSize: "0.85rem",
                              lineHeight: "1",
                              fontWeight: 600,
                              pointerEvents: "none",
                              whiteSpace: "nowrap",
                              zIndex: 1,
                            }}
                          >
                            NZ$
                          </span>
                          <input
                            type={inputType}
                            inputMode="decimal"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={displayValue}
                            onChange={handleCellChange}
                            onBlur={handleCellBlur}
                            className={hasError ? "error" : ""}
                            required={isRequired}
                            style={{ paddingLeft: "3.25rem", boxSizing: "border-box" }}
                          />
                        </div>
                      ) : (
                        <input
                          type={inputType}
                          value={cellValue}
                          maxLength={columnMaxCharacters}
                          onChange={e => {
                            const nextValue = columnMaxCharacters
                              ? e.target.value.slice(0, columnMaxCharacters)
                              : e.target.value;
                            updateCell(rowIndex, columnId, nextValue, columnType);
                          }}
                          className={hasError ? "error" : ""}
                          required={isRequired}
                        />
                      )}
                    </td>
                  );
                })}
                <td data-label="">
                  <button
                    type="button"
                    className="remove-row"
                    onClick={() => removeRow(rowIndex)}
                    aria-label="Remove row"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} style={{ textAlign: "center", padding: "2rem" }}>
                  No rows added yet. Click "Add Row" to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="table-actions">
        <button type="button" onClick={addRow}>
          Add Row
        </button>
      </div>
      {error && <span className="error-message">{error}</span>}
    </TableFieldWrapper>
  );
};

export interface FieldProps {
  field: PermitFormField;
  value:
  | string
  | string[]
  | File
  | File[]
  | UploadedFileRecord[]
  | Array<Record<string, string>>
  | undefined;
  onChange: (value: string | string[] | File | File[] | Array<Record<string, string>> | null) => void;
  error?: string | null;
  fileUploads?: UploadedFileRecord[];
  onAddFiles?: (files: File[]) => void;
  onRemoveFile?: (fileId: string) => void;
  onRetryFile?: (fileId: string) => void;
}

/** Renders a single form control (label + input/select/textarea + error). No layout wrapper. */
export const Field = ({
  field,
  value,
  onChange,
  error,
  fileUploads = [],
  onAddFiles,
  onRemoveFile,
  onRetryFile,
}: FieldProps) => {
  const fieldName = getFieldKey(field);
  const fieldType = field.type || "text";
  const fieldLabel = field.label || "";
  const fieldOptions = field.options || [];
  const required = Boolean(field.required);
  const maxCharacters = normalizeMaxCharacters(field.maxCharacters);
  const isCharacterCountField = ["text", "email", "tel", "textarea"].includes(fieldType) && maxCharacters !== undefined;
  const currentTextLength = typeof value === "string" ? value.length : 0;
  const sharedProps = {
    id: fieldName,
    name: fieldName,
    className: error ? "error" : "",
    required,
    "aria-required": required,
    "aria-invalid": Boolean(error),
  };
  const labelContent = (
    <>
      {fieldLabel}
      {required && (
        <span className="form-field-required" aria-hidden="true">
          {" "}
          *
        </span>
      )}
    </>
  );

  if (fieldType === "boolean") {
    const boolValue = value === "true" || value === "false" ? (value as string) : "";
    return (
      <>
        <label>{labelContent}</label>
        <div className="radio-group">
          <div className="radio-item">
            <input
              type="radio"
              id={`${fieldName}-yes`}
              name={fieldName}
              value="true"
              checked={boolValue === "true"}
              onChange={() => onChange("true")}
            />
            <label htmlFor={`${fieldName}-yes`}>Yes</label>
          </div>
          <div className="radio-item">
            <input
              type="radio"
              id={`${fieldName}-no`}
              name={fieldName}
              value="false"
              checked={boolValue === "false"}
              onChange={() => onChange("false")}
            />
            <label htmlFor={`${fieldName}-no`}>No</label>
          </div>
        </div>
        {error && <span className="error-message">{error}</span>}
      </>
    );
  }

  if (fieldType === "file") {
    return (
      <>
        <label htmlFor={fieldName}>{labelContent}</label>
        <DragAndDropFileInput
          id={fieldName}
          name={fieldName}
          accept={field.accept ?? undefined}
          value={fileUploads}
          onAddFiles={files => {
            if (onAddFiles) onAddFiles(files);
          }}
          onRemoveFile={fileId => {
            if (onRemoveFile) onRemoveFile(fileId);
          }}
          onRetryFile={fileId => {
            if (onRetryFile) onRetryFile(fileId);
          }}
          error={Boolean(error)}
          multiple={true}
        />
        {error && <span className="error-message">{error}</span>}
      </>
    );
  }

  if (fieldType === "select") {
    return (
      <>
        <label htmlFor={fieldName}>{labelContent}</label>
        <select {...sharedProps} value={value as string} onChange={e => onChange(e.target.value)}>
          <option value="">Please select</option>
          {fieldOptions.map((option, index) => (
            <option key={index} value={option || ""}>
              {option}
            </option>
          ))}
        </select>
        {error && <span className="error-message">{error}</span>}
      </>
    );
  }

  if (fieldType === "checkbox") {
    const checkboxValues: string[] = Array.isArray(value)
      ? (value as unknown[]).filter((v): v is string => typeof v === "string")
      : [];
    return (
      <>
        <label>{labelContent}</label>
        <div className="checkbox-group">
          {fieldOptions.map((option, index) => {
            const optionValue = option || "";
            const isChecked = checkboxValues.includes(optionValue);
            return (
              <div key={index} className="checkbox-item">
                <input
                  type="checkbox"
                  id={`${fieldName}-${index}`}
                  checked={isChecked}
                  onChange={e => {
                    if (e.target.checked) {
                      onChange([...checkboxValues, optionValue]);
                    } else {
                      onChange(checkboxValues.filter(v => v !== optionValue));
                    }
                  }}
                />
                <label htmlFor={`${fieldName}-${index}`}>{option}</label>
              </div>
            );
          })}
        </div>
        {error && <span className="error-message">{error}</span>}
      </>
    );
  }

  if (fieldType === "radio") {
    return (
      <>
        <label>{labelContent}</label>
        <div className="radio-group">
          {fieldOptions.map((option, index) => (
            <div key={index} className="radio-item">
              <input
                type="radio"
                id={`${fieldName}-${index}`}
                name={fieldName}
                value={option || ""}
                checked={value === option}
                onChange={e => onChange(e.target.value)}
              />
              <label htmlFor={`${fieldName}-${index}`}>{option}</label>
            </div>
          ))}
        </div>
        {error && <span className="error-message">{error}</span>}
      </>
    );
  }
  if (fieldType === "amount") {
    return (
      <AmountField
        fieldName={fieldName}
        labelContent={labelContent}
        value={value as string}
        onChange={onChange}
        error={error}
        sharedProps={sharedProps}
      />
    );
  }
  if (fieldType === "textarea") {
    return (
      <>
        <label htmlFor={fieldName}>{labelContent}</label>
        <textarea
          {...sharedProps}
          maxLength={maxCharacters}
          value={value as string}
          onChange={e => {
            const nextValue = e.target.value;
            onChange(maxCharacters ? nextValue.slice(0, maxCharacters) : nextValue);
          }}
        />
        {isCharacterCountField && maxCharacters !== undefined && (
          <CharacterCount current={currentTextLength} max={maxCharacters} />
        )}
        {error && <span className="error-message">{error}</span>}
      </>
    );
  }

  if (fieldType === "table") {
    const tableValue = Array.isArray(value) && value.length > 0 && typeof value[0] === "object" && !(value[0] instanceof File)
      ? (value as Array<Record<string, string>>)
      : undefined;
    return (
      <TableField
        field={field}
        value={tableValue}
        onChange={onChange as (value: Array<Record<string, string>> | null) => void}
        error={error}
      />
    );
  }

  return (
    <>
      <label htmlFor={fieldName}>{labelContent}</label>
      <input
        {...sharedProps}
        maxLength={maxCharacters}
        type={fieldType === "text" ? "text" : fieldType}
        value={value as string}
        onChange={e => {
          const nextValue = e.target.value;
          onChange(maxCharacters ? nextValue.slice(0, maxCharacters) : nextValue);
        }}
      />
      {isCharacterCountField && maxCharacters !== undefined && (
        <CharacterCount current={currentTextLength} max={maxCharacters} />
      )}
      {error && <span className="error-message">{error}</span>}
    </>
  );
};

export interface FormFieldsProps {
  fields: PermitFormField[];
  inputs: Record<string, any>;
  onChange: (key: string, value: any) => void;
  fileUploadsByField?: Record<string, UploadedFileRecord[]>;
  onFileAdd?: (key: string, files: File[]) => void;
  onFileRemove?: (key: string, fileId: string) => void;
  onFileRetry?: (key: string, fileId: string) => void;
}

/** Renders a list of form fields, each wrapped for layout. Fields with a hidden condition are skipped when the condition is met. */
const FormFields = ({
  fields,
  inputs,
  onChange,
  fileUploadsByField = {},
  onFileAdd,
  onFileRemove,
  onFileRetry,
}: FormFieldsProps) => {
  if (!fields?.length) return null;

  const visibleFields = fields.filter(field => field && !isFieldHidden(field, inputs));

  return (
    <div className="form-fields">
      {visibleFields.map((field, index) =>
        field ? (
          <FieldWrapper
            key={field._key ?? getFieldKey(field, index)}
            fullWidth={field.fullWidth || field.type === "table"}
            error={inputs[`${getFieldKey(field, index)}Error`]}
          >
            <Field
              field={field}
              value={
                inputs[getFieldKey(field, index)] ??
                (field.type === "checkbox"
                  ? []
                  : field.type === "file"
                    ? undefined
                    : field.type === "boolean"
                      ? ""
                      : field.type === "table"
                        ? undefined
                        : "")
              }
              onChange={value => onChange(getFieldKey(field, index), value)}
              error={inputs[`${getFieldKey(field, index)}Error`]}
              fileUploads={fileUploadsByField[getFieldKey(field, index)] ?? []}
              onAddFiles={files => onFileAdd?.(getFieldKey(field, index), files)}
              onRemoveFile={fileId => onFileRemove?.(getFieldKey(field, index), fileId)}
              onRetryFile={fileId => onFileRetry?.(getFieldKey(field, index), fileId)}
            />
          </FieldWrapper>
        ) : null,
      )}
    </div>
  );
};

export default FormFields;
